import { useState, useCallback } from 'react';
import type { StorageApi } from '@/lib/api';

/**
 * Uploads a single file via presigned URL.
 * Returns the CDN URL on success, or throws on failure.
 */
async function uploadSingleFile(
  api: StorageApi,
  file: File,
  key: string,
): Promise<{ key: string; cdnUrl: string | null }> {
  // Browsers return empty string for unrecognized extensions (.bundle, .hash, etc.)
  const contentType = file.type || 'application/octet-stream';

  const { uploadUrl, cdnUrl } = await api.getUploadUrl(key, contentType, file.size);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  return { key, cdnUrl };
}

/** Max concurrent uploads to avoid overwhelming the browser/network */
const UPLOAD_CONCURRENCY = 5;

export interface UploadProgress {
  total: number;
  completed: number;
  failed: number;
  inProgress: boolean;
}

export interface BulkUploadResult {
  succeeded: number;
  failed: string[];
}

export interface FailedFileEntry {
  file: File;
  key: string;
  reason?: string;
}

/**
 * Processes an array of file entries in concurrent batches.
 * Returns the count of successes and the entries that failed.
 */
async function processBatches(
  api: StorageApi,
  entries: FailedFileEntry[],
  onBatchComplete: (batchSucceeded: number, batchFailed: FailedFileEntry[]) => void,
): Promise<{ succeeded: number; failed: FailedFileEntry[] }> {
  let succeeded = 0;
  const failed: FailedFileEntry[] = [];

  for (let i = 0; i < entries.length; i += UPLOAD_CONCURRENCY) {
    const batch = entries.slice(i, i + UPLOAD_CONCURRENCY);
    const results = await Promise.allSettled(
      batch.map(({ file, key }) => uploadSingleFile(api, file, key)),
    );

    let batchSucceededCount = 0;
    const batchFailed: FailedFileEntry[] = [];

    for (let j = 0; j < results.length; j++) {
      const result = results[j];
      if (result.status === 'fulfilled') {
        batchSucceededCount++;
      } else {
        batchFailed.push({
          ...batch[j],
          reason: result.reason instanceof Error ? result.reason.message : String(result.reason),
        });
      }
    }

    succeeded += batchSucceededCount;
    failed.push(...batchFailed);
    onBatchComplete(batchSucceededCount, batchFailed);
  }

  return { succeeded, failed };
}

/**
 * Returns bulk upload functions for a storage arche.
 * Uploads files concurrently (up to UPLOAD_CONCURRENCY at a time),
 * continues on per-file errors, and reports progress via callback.
 * Failed files are stored in state for user-controlled retry.
 */
export function useStorageUpload(api: StorageApi) {
  const [failedFiles, setFailedFiles] = useState<FailedFileEntry[]>([]);

  const uploadFiles = useCallback(
    async (
      files: { file: File; key: string }[],
      onProgress?: (progress: UploadProgress) => void,
    ): Promise<BulkUploadResult> => {
      const total = files.length;
      let completed = 0;
      const failedEntries: FailedFileEntry[] = [];

      // Clear any previous failures
      setFailedFiles([]);

      onProgress?.({ total, completed, failed: 0, inProgress: true });

      const { succeeded } = await processBatches(api, files, (batchSucceeded, batchFailed) => {
        completed += batchSucceeded;
        failedEntries.push(...batchFailed);
        onProgress?.({
          total,
          completed,
          failed: failedEntries.length,
          inProgress: true,
        });
      });

      // Store failed entries for user-controlled retry
      setFailedFiles(failedEntries);

      onProgress?.({
        total,
        completed,
        failed: failedEntries.length,
        inProgress: false,
      });

      return { succeeded, failed: failedEntries.map((e) => e.key) };
    },
    [api],
  );

  const retryFailedFiles = useCallback(
    async (onProgress?: (progress: UploadProgress) => void): Promise<BulkUploadResult> => {
      const entries = failedFiles;
      if (entries.length === 0) {
        return { succeeded: 0, failed: [] };
      }

      const total = entries.length;
      let completed = 0;
      const stillFailed: FailedFileEntry[] = [];

      onProgress?.({ total, completed, failed: 0, inProgress: true });

      const { succeeded } = await processBatches(api, entries, (batchSucceeded, batchFailed) => {
        completed += batchSucceeded;
        stillFailed.push(...batchFailed);
        onProgress?.({
          total,
          completed,
          failed: stillFailed.length,
          inProgress: true,
        });
      });

      // Update state to only those that still failed
      setFailedFiles(stillFailed);

      onProgress?.({
        total,
        completed,
        failed: stillFailed.length,
        inProgress: false,
      });

      return { succeeded, failed: stillFailed.map((e) => e.key) };
    },
    [api, failedFiles],
  );

  const clearFailedFiles = useCallback(() => {
    setFailedFiles([]);
  }, []);

  return { uploadFiles, retryFailedFiles, clearFailedFiles, failedFiles };
}
